import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import StatisticsCardLoading from '../../common/statistic-chart-card/StatisticsChartCardLoading';
import StatisticsChartEmpty from '../../common/StatisticsChartEmpty';
import { STATISTICS_EMPTY_CHART_COMPARE_MAP_RANGE, STATISTICS_EMPTY_CHART_COMPARE_MAP_YEAR } from '../../common/lib/statisticsEmptyStateCopy';
import { PERIOD_YOY_GRANULARITY } from '../../filters/lib/statisticsPeriodYoyHelpers';
import OrderPrimaryPhaseChinaMap from '../pipeline/OrderPrimaryPhaseChinaMap';
import { normalizeTrendDateField } from '../../filters/lib/statisticsTrendDimension';
import { buildLineCompareMapPeriods, filterOrdersForLineCompareMapPeriod } from './lib/trendMap';

const DEFAULT_MAP_HEIGHT = '8.5rem';
const COMPACT_MAP_HEIGHT_REM = 12;

const resolveMapCompactMode = (height = DEFAULT_MAP_HEIGHT) => {
	const match = /^([\d.]+)rem$/.exec(String(height).trim());
	if (!match) return true;
	return Number(match[1]) < COMPACT_MAP_HEIGHT_REM;
};

const TrendMapChart = ({
	orders = [],
	compares = [],
	trendYearMonthsMode = false,
	trendGranularity = PERIOD_YOY_GRANULARITY.MONTH,
	trendPeriodIndices = null,
	trendMonthIndices = null,
	trendPeriodYoy = null,
	selectedPhase = null,
	highlightProvinceNames = [],
	highlightAnchorCityRows = [],
	isLoading = false,
	animationKey = 'default',
	height = DEFAULT_MAP_HEIGHT,
	fillSlot = false,
	trendDateField = 'createdAt',
	enableOrderDrill = false,
	onDrillSelect,
}) => {
	const periodIndices = trendPeriodIndices ?? trendMonthIndices;
	const resolvedTrendDateField = normalizeTrendDateField(trendDateField);

	const mapPeriods = useMemo(
		() =>
			buildLineCompareMapPeriods({
				compares,
				yearMonthsMode: trendYearMonthsMode,
				periodYoy: trendPeriodYoy,
				granularity: trendGranularity,
				periodIndices,
			}),
		[compares, trendGranularity, trendPeriodYoy, trendYearMonthsMode, periodIndices],
	);

	const periodOrders = useMemo(
		() =>
			mapPeriods.map((period) => ({
				...period,
				orders: filterOrdersForLineCompareMapPeriod({
					orders,
					period,
					selectedPhase,
					trendDateField: resolvedTrendDateField,
				}),
			})),
		[mapPeriods, orders, resolvedTrendDateField, selectedPhase],
	);

	const hasAnchor = useMemo(() => compares.some((item) => item.kind === 'anchor'), [compares]);
	const customCompareCount = useMemo(() => compares.filter((item) => item.kind === 'custom').length, [compares]);
	const hasMapData = periodOrders.length > 0 && periodOrders.some((item) => item.orders.length > 0);
	const layoutCompact = resolveMapCompactMode(height);
	/** 统计页小预览只展示首段；首页固定卡片需保留全部对比地图 */
	const showSingleMapPreview = layoutCompact && !fillSlot;
	const displayPeriods = showSingleMapPreview ? periodOrders.slice(0, 1) : periodOrders;

	if (isLoading) {
		if (fillSlot) {
			return (
				<div className='statistics-trend-chart statistics-trend-chart--preview' style={{ height: '100%', width: '100%', minHeight: 0 }}>
					<StatisticsCardLoading className='statistics-lite-chart__loading statistics-lite-chart__loading--fill' fill />
				</div>
			);
		}

		return <StatisticsCardLoading className='statistics-lite-chart__loading' style={{ minHeight: height }} />;
	}

	if (!hasMapData) {
		return (
			<div className='statistics-trend-chart'>
				<StatisticsChartEmpty>
					{trendYearMonthsMode
						? STATISTICS_EMPTY_CHART_COMPARE_MAP_YEAR
						: !hasAnchor
							? '先选基准区间，再添加至少 1 个对比段'
							: customCompareCount === 0
								? '基准已选，请点按年或按季/月对比后自选对比段'
								: STATISTICS_EMPTY_CHART_COMPARE_MAP_RANGE}
				</StatisticsChartEmpty>
			</div>
		);
	}

	const gridClassName = ['statistics-trend-map-charts__grid', displayPeriods.length === 1 ? 'statistics-trend-map-charts__grid--single' : null].filter(Boolean).join(' ');

	return (
		<div className={['statistics-trend-chart statistics-trend-map-charts', fillSlot ? 'statistics-trend-map-charts--fill-slot' : null].filter(Boolean).join(' ')}>
			<div className={gridClassName}>
				{displayPeriods.map((period) => (
					<section key={`${period.id}-${animationKey}`} className='statistics-trend-map-charts__item'>
						{showSingleMapPreview || fillSlot ? null : <h4 className='statistics-trend-map-charts__item-title mb-0'>{period.label}</h4>}
						<OrderPrimaryPhaseChinaMap
							orders={period.orders}
							highlightProvinceNames={highlightProvinceNames}
							highlightAnchorCityRows={highlightAnchorCityRows}
							isLoading={false}
							animationKey={`${animationKey}-${period.id}`}
							height={height}
							compact={layoutCompact}
							fillSlot={fillSlot}
							enableOrderDrill={enableOrderDrill}
							onDrillSelect={
								enableOrderDrill && onDrillSelect
									? (drill) =>
											onDrillSelect({
												...drill,
												startDate: period.startDate ?? (period.year ? `${period.year}-01-01` : undefined),
												endDate: period.endDate ?? (period.year ? `${period.year}-12-31` : undefined),
												dateField: resolvedTrendDateField,
												label: period.label && drill?.label ? `${period.label} · ${drill.label}` : drill?.label,
											})
									: undefined
							}
						/>
					</section>
				))}
			</div>
		</div>
	);
};

TrendMapChart.propTypes = {
	orders: PropTypes.array,
	compares: PropTypes.arrayOf(PropTypes.object),
	trendYearMonthsMode: PropTypes.bool,
	trendGranularity: PropTypes.string,
	trendPeriodIndices: PropTypes.arrayOf(PropTypes.number),
	trendMonthIndices: PropTypes.arrayOf(PropTypes.number),
	trendPeriodYoy: PropTypes.object,
	selectedPhase: PropTypes.string,
	highlightProvinceNames: PropTypes.arrayOf(PropTypes.string),
	highlightAnchorCityRows: PropTypes.array,
	isLoading: PropTypes.bool,
	animationKey: PropTypes.string,
	height: PropTypes.string,
	fillSlot: PropTypes.bool,
	trendDateField: PropTypes.string,
	enableOrderDrill: PropTypes.bool,
	onDrillSelect: PropTypes.func,
};

export default memo(TrendMapChart);
